import { useNavigate } from 'react-router-dom';
import { Box, Stack, Table, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Card } from '@liminal/web-ui/primitives/Card';
import { Button } from '@liminal/web-ui/primitives/Button';
import { StatusPill } from '@liminal/web-ui/primitives/StatusPill';
import { Select } from '@liminal/web-ui/forms/Select';
import { DataTableBody } from '@liminal/web-ui/display/DataTableBody';
import { EmptyState } from '@liminal/web-ui/feedback/EmptyState';
import { CursorPagination } from '@liminal/web-ui/primitives/Pagination';
import { useCursorPagination } from '@liminal/web-ui/primitives/useCursorPagination';
import { formatOrderDate } from '@liminal/utils/date';
import { RewardBudget_State, RewardMerchant_Category, RewardMerchant_State } from '@/api/generated';
import { useNow } from '@/hooks/useNow';
import { useRewardBudgets, useRewardMerchants } from '@/hooks/useRewards';
import { useUrlState } from '@/hooks/useUrlState';
import { merchantAvailabilityPill, merchantCategory, merchantState } from '@/components/ui/statusHelpers';
import {
  cashbackPercent,
  groupDecimal,
  merchantAvailability,
  merchantCategoryOf,
  merchantFilter,
  resourceId,
} from '@/lib/rewards';

const PAGE_SIZE = 50;
const COLUMNS = ['Partner', 'Category', 'Status', 'Pay with LIL', 'Earn', 'Cashback', 'Budget', 'Updated'] as const;

/** Budgets are a handful; one page names every partner's. */
const BUDGET_NAMES_PAGE = 200;

const FILTER_DEFAULTS = { category: '', state: '' };

const CATEGORY_OPTIONS = [
  { value: '', label: 'Any category' },
  ...Object.values(RewardMerchant_Category).map((category) => ({ value: category, label: merchantCategory(category).label })),
];

// By the partner's state, which the server has always filtered on. Whether
// an active partner is in or between its windows is worked out per row, so
// it is a pill rather than a filter.
const STATE_OPTIONS = [
  { value: '', label: 'Any state' },
  ...Object.values(RewardMerchant_State).map((state) => ({ value: state, label: merchantState(state).label })),
];

export function PartnersListPage() {
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlState(FILTER_DEFAULTS);
  const pagination = useCursorPagination(PAGE_SIZE);
  const { data, isLoading, isError } = useRewardMerchants(PAGE_SIZE, pagination.pageToken ?? '', merchantFilter(filters));
  const budgets = useRewardBudgets(BUDGET_NAMES_PAGE, '');
  // Whether a partner is between windows moves with the clock.
  const now = useNow(60_000);
  const budgetNames = new Map((budgets.data?.rewardBudgets ?? []).map((b) => [b.name, b.displayName ?? b.name]));
  const hasLiveBudget = (budgets.data?.rewardBudgets ?? []).some((b) => b.state !== RewardBudget_State.STATE_CLOSED);
  const merchants = data?.rewardMerchants ?? [];
  const nextPageToken = data?.nextPageToken ?? '';
  const filtered = Object.values(filters).some((value) => value !== '');
  const none = !isLoading && !isError && merchants.length === 0 && !pagination.hasPreviousPage && !filtered;

  // A page token belongs to the filter that produced it, so a new filter starts again at page one.
  const setFilter = (patch: Partial<typeof FILTER_DEFAULTS>) => {
    setFilters(patch);
    pagination.reset();
  };

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} justifyContent="space-between" alignItems="center">
        <Typography variant="body2" color="text.secondary">
          Merchants where a Pix payment lets the customer earn LIL or pay with it.
        </Typography>
        <Button startIcon="plus" onClick={() => navigate('/rewards/partners/new')}>
          New partner
        </Button>
      </Stack>

      {none ? (
        <Card variant="elevated">
          {budgets.isSuccess && !hasLiveBudget ? (
            <EmptyState
              icon="gift"
              title="Add a budget first"
              subtitle="Every partner draws its Pay with LIL payments from a budget."
              action={{ label: 'New budget', onClick: () => navigate('/rewards/budgets/new') }}
            />
          ) : (
            <EmptyState
              icon="gift"
              title="No partners yet"
              subtitle="Add the first merchant customers can earn or pay with LIL at."
              action={{ label: 'New partner', onClick: () => navigate('/rewards/partners/new') }}
            />
          )}
        </Card>
      ) : (
        <>
          <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap" alignItems="center">
            <Select
              variant="filter"
              aria-label="Category"
              size="sm"
              width={200}
              value={filters.category}
              onChange={(category) => setFilter({ category })}
              options={CATEGORY_OPTIONS}
            />
            <Select
              variant="filter"
              aria-label="State"
              size="sm"
              width={180}
              value={filters.state}
              onChange={(state) => setFilter({ state })}
              options={STATE_OPTIONS}
            />
            {filtered && (
              <Box sx={{ height: 40, display: 'flex', alignItems: 'center' }}>
                <Button variant="link" size="sm" onClick={() => setFilter(FILTER_DEFAULTS)}>
                  Clear
                </Button>
              </Box>
            )}
          </Stack>

          <Card variant="elevated" padding="none">
            <Table>
              <TableHead>
                <TableRow>
                  {COLUMNS.map((column) => (
                    <TableCell key={column}>{column}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <DataTableBody
                columns={COLUMNS.length}
                isLoading={isLoading}
                isError={isError}
                isEmpty={merchants.length === 0}
                loadingRows={6}
                emptyMessage={filtered ? 'No partners match these filters.' : 'No partners on this page.'}
                errorMessage="Could not load partners."
              >
                {merchants.map((m) => {
                  const status = merchantAvailabilityPill(merchantAvailability(m, now));
                  const category = merchantCategoryOf(m.category);
                  const cashback = m.rates ? cashbackPercent(m.rates.earnLilPerUsd, m.rates.redeemLilPerUsd) : null;
                  return (
                    <TableRow
                      key={m.name}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/rewards/partners/${resourceId(m.name, 'rewardMerchants')}`)}
                    >
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Typography variant="body2">{m.displayName}</Typography>
                        {m.description && (
                          <Typography variant="caption" color="text.secondary" noWrap component="div">
                            {m.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{category === null ? '—' : merchantCategory(category).label}</TableCell>
                      <TableCell>
                        <StatusPill tone={status.tone} label={status.label} size="sm" />
                      </TableCell>
                      <TableCell>{m.rates ? `${groupDecimal(m.rates.redeemLilPerUsd)} LIL per $1` : '—'}</TableCell>
                      <TableCell>{m.rates ? `${groupDecimal(m.rates.earnLilPerUsd)} LIL per $1` : '—'}</TableCell>
                      <TableCell>{cashback === null ? '—' : `${cashback}%`}</TableCell>
                      <TableCell>{m.budget ? (budgetNames.get(m.budget) ?? m.budget) : '—'}</TableCell>
                      <TableCell>{m.updateTime ? formatOrderDate(m.updateTime) : '—'}</TableCell>
                    </TableRow>
                  );
                })}
              </DataTableBody>
            </Table>
          </Card>
        </>
      )}

      {!none && (
        <CursorPagination
          firstRowNumber={pagination.firstRowNumber}
          count={merchants.length}
          hasPreviousPage={pagination.hasPreviousPage}
          hasNextPage={!!nextPageToken}
          onPreviousPage={pagination.goToPreviousPage}
          onNextPage={() => pagination.goToNextPage(nextPageToken)}
          disabled={isLoading}
        />
      )}
    </Stack>
  );
}
